'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { ChevronDownIcon, PlusIcon } from '@radix-ui/react-icons';
import * as React from 'react';
import { mergeAriaIds } from '../../../lib/aria';
import { useControlledLatch } from '../../../lib/controlled-state';
import { developmentDiagnosticsEnabled } from '../../../lib/diagnostics';
import { createRenderIdentityPlan } from '../../../lib/render-identities';
import { matchesSearchTerms } from '../../../lib/search';
import { cn } from '../../../lib/utils';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { SpecraButton } from '../ensigns/button';
import { SpecraText } from '../ensigns/typography';
/**
 * Searchable picker chassis shared by contacts, roles, files, and organizations.
 * Domain fetching and option construction stay with the consuming vertical.
 */ function SpecraComboboxInner({ className, canCreate, clearable = false, clearLabel = 'Clear selection', contentProps, contentClassName, createErrorMessage = 'Could not create this option. Try again.', createLabel = (query)=>`Create “${query}”`, createLoading, createLoadingLabel = 'Creating…', createTestId, defaultOpen = false, defaultSearchValue = '', defaultValue, disabled, emptyMessage = 'No options found.', fallbackLabel, form, getOptionProps, getOptionTestId, hiddenInputProps, id, inputProps, label, loading = false, loadingMessage = 'Loading…', name, onChange, onCreate, onCreateError, onOpenChange, onOptionChange, onSearchValueChange, onValueChange, open, options, placeholder = 'Select…', portalContainer, required, searchValue, selectedOption, searchPlaceholder = 'Search…', shouldFilter = true, triggerProps, validationMessage = `${label} is required.`, value }, forwardedRef) {
    const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
    const [internalQuery, setInternalQuery] = React.useState(defaultSearchValue);
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const [internalCreating, setInternalCreating] = React.useState(false);
    const createInFlightRef = React.useRef(false);
    const [createError, setCreateError] = React.useState();
    const [showValidationError, setShowValidationError] = React.useState(false);
    const [renderedListId, setRenderedListId] = React.useState();
    const valueControlled = useControlledLatch(value !== undefined);
    const openControlled = useControlledLatch(open !== undefined);
    const queryControlled = useControlledLatch(searchValue !== undefined);
    const triggerRef = React.useRef(null);
    const generatedId = React.useId();
    const contentId = contentProps?.id ?? `${id ?? generatedId}-options`;
    const validationId = `${id ?? generatedId}-validation`;
    const resolvedOpen = openControlled ? open ?? false : internalOpen;
    const query = queryControlled ? searchValue ?? '' : internalQuery;
    const selectedValue = valueControlled ? value : internalValue;
    const selected = options.find((option)=>option.value === selectedValue) ?? (selectedOption?.value === selectedValue ? selectedOption : undefined);
    const selectedAccessibleText = selected?.textValue ?? (typeof selected?.label === 'string' ? selected.label : selected ? typeof fallbackLabel === 'string' ? fallbackLabel : 'Selected option' : typeof fallbackLabel === 'string' ? fallbackLabel : placeholder);
    const normalizedQuery = query.trim();
    const isCreating = Boolean(createLoading || internalCreating);
    const canOfferCreate = Boolean(onCreate && normalizedQuery);
    const optionIdentityPlan = React.useMemo(()=>createRenderIdentityPlan(options.map((option)=>option.id ?? option.value), (optionId)=>optionId.trim().toLocaleLowerCase()), [
        options
    ]);
    const duplicateOptionIdSignature = developmentDiagnosticsEnabled ? optionIdentityPlan.duplicateIds.join('\u0000') : '';
    const unsearchableOptionIds = React.useMemo(()=>developmentDiagnosticsEnabled ? options.filter((option)=>typeof option.label !== 'string' && !option.textValue && (option.keywords?.length ?? 0) === 0).map((option)=>option.id ?? option.value).join(', ') : '', [
        options
    ]);
    const unnamedOptionIds = React.useMemo(()=>developmentDiagnosticsEnabled ? options.filter((option)=>typeof option.label !== 'string' && !option.textValue).map((option)=>option.id ?? option.value).join(', ') : '', [
        options
    ]);
    React.useEffect(()=>{
        if (developmentDiagnosticsEnabled && shouldFilter && unsearchableOptionIds) {
            console.warn(`SpecraCombobox options with custom labels need textValue or keywords for search: ${unsearchableOptionIds}.`);
        }
    }, [
        shouldFilter,
        unsearchableOptionIds
    ]);
    React.useEffect(()=>{
        if (developmentDiagnosticsEnabled && unnamedOptionIds) {
            console.warn(`SpecraCombobox options with custom labels need textValue for an exact accessible name: ${unnamedOptionIds}.`);
        }
    }, [
        unnamedOptionIds
    ]);
    React.useEffect(()=>{
        if (developmentDiagnosticsEnabled && duplicateOptionIdSignature) {
            console.error(`SpecraCombobox received duplicate option ids after cmdk normalization: ${optionIdentityPlan.duplicateIds.join(', ')}. Option ids must be unique and cannot differ only by case or surrounding whitespace; render-only fallback identities were applied.`);
        }
    }, [
        duplicateOptionIdSignature,
        optionIdentityPlan.duplicateIds
    ]);
    const filterSearchText = React.useCallback((optionValue, search, keywords = [])=>{
        // cmdk values are stable option identities. User-facing text is supplied
        // explicitly below so opaque ids do not become accidental search terms.
        const clearSearchText = optionValue === '__specra_clear__' ? typeof clearLabel === 'string' ? clearLabel : 'clear selection' : '';
        return matchesSearchTerms(search, [
            clearSearchText,
            ...keywords
        ]);
    }, [
        clearLabel
    ]);
    const duplicateOption = canOfferCreate && options.some((option)=>{
        const optionText = option.textValue ?? (typeof option.label === 'string' ? option.label : undefined);
        const normalizedOptionText = optionText?.toLocaleLowerCase();
        const normalizedKeywords = option.keywords?.map((keyword)=>keyword.toLocaleLowerCase());
        const normalizedCandidate = normalizedQuery.toLocaleLowerCase();
        return normalizedOptionText === normalizedCandidate || normalizedKeywords?.includes(normalizedCandidate);
    });
    const showCreate = canOfferCreate && !isCreating && (canCreate ? canCreate(normalizedQuery, options) : !duplicateOption);
    const submissionDisabled = disabled || hiddenInputProps?.disabled;
    const needsRequiredValidation = Boolean(required && !submissionDisabled && !selectedValue);
    const updateOpen = (nextOpen)=>{
        if (!openControlled) setInternalOpen(nextOpen);
        onOpenChange?.(nextOpen);
    };
    React.useEffect(()=>{
        if (selectedValue) setShowValidationError(false);
    }, [
        selectedValue
    ]);
    const updateQuery = (nextQuery)=>{
        if (!queryControlled) setInternalQuery(nextQuery);
        setCreateError(undefined);
        onSearchValueChange?.(nextQuery);
    };
    const handleListRef = React.useCallback((node)=>setRenderedListId(node?.id), []);
    const commit = (nextValue, nextOption)=>{
        if (!valueControlled) setInternalValue(nextValue);
        onValueChange?.(nextValue);
        onChange?.(nextValue);
        onOptionChange?.(nextOption ?? options.find((option)=>option.value === nextValue) ?? (selectedOption?.value === nextValue ? selectedOption : undefined));
        setShowValidationError(false);
        updateOpen(false);
        updateQuery('');
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            name || needsRequiredValidation ? /*#__PURE__*/ _jsx("input", {
                ...hiddenInputProps,
                "aria-hidden": needsRequiredValidation ? true : hiddenInputProps?.['aria-hidden'],
                "aria-label": needsRequiredValidation ? undefined : hiddenInputProps?.['aria-label'],
                className: cn(hiddenInputProps?.className, needsRequiredValidation && 'pointer-events-none absolute size-px opacity-0'),
                disabled: submissionDisabled,
                form: form,
                name: name,
                onChange: (event)=>hiddenInputProps?.onChange?.(event),
                onInvalid: (event)=>{
                    hiddenInputProps?.onInvalid?.(event);
                    if (!needsRequiredValidation) return;
                    event.preventDefault();
                    setShowValidationError(true);
                    triggerRef.current?.focus();
                },
                required: needsRequiredValidation,
                tabIndex: needsRequiredValidation ? -1 : hiddenInputProps?.tabIndex,
                type: needsRequiredValidation ? 'text' : 'hidden',
                value: selectedValue ?? ''
            }) : null,
            /*#__PURE__*/ _jsxs(Popover, {
                open: resolvedOpen,
                onOpenChange: updateOpen,
                children: [
                    /*#__PURE__*/ _jsx(PopoverTrigger, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsxs(SpecraButton, {
                            ...triggerProps,
                            ref: (node)=>{
                                triggerRef.current = node;
                                if (typeof forwardedRef === 'function') forwardedRef(node);
                                else if (forwardedRef) {
                                    forwardedRef.current = node;
                                }
                            },
                            "aria-controls": resolvedOpen ? renderedListId : undefined,
                            "aria-describedby": mergeAriaIds(triggerProps?.['aria-describedby'], showValidationError ? validationId : undefined),
                            "aria-expanded": resolvedOpen,
                            "aria-haspopup": "listbox",
                            "aria-invalid": showValidationError ? true : triggerProps?.['aria-invalid'] ?? undefined,
                            "aria-label": triggerProps?.['aria-label'] ?? `${label}: ${selectedAccessibleText}`,
                            "aria-required": required || undefined,
                            role: "combobox",
                            className: cn('w-full justify-between bg-card font-normal', !selected && !fallbackLabel && 'text-muted-foreground', triggerProps?.className, className),
                            disabled: disabled || triggerProps?.disabled,
                            id: id,
                            variant: "outline",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "min-w-0 truncate text-left",
                                    children: selected?.label ?? fallbackLabel ?? placeholder
                                }),
                                /*#__PURE__*/ _jsx(ChevronDownIcon, {
                                    "aria-hidden": "true",
                                    className: "shrink-0 opacity-60"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(PopoverContent, {
                        ...contentProps,
                        align: "start",
                        className: cn('w-[var(--radix-popover-trigger-width)] rounded-[var(--radius-md)] border-[var(--border-card)] bg-[var(--surface-panel)] p-[var(--space-1)] shadow-[var(--shadow-md)]', contentProps?.className, contentClassName),
                        id: contentId,
                        portalContainer: portalContainer,
                        children: /*#__PURE__*/ _jsxs(Command, {
                            "aria-busy": loading || isCreating,
                            filter: filterSearchText,
                            label: label,
                            shouldFilter: shouldFilter,
                            children: [
                                /*#__PURE__*/ _jsx(CommandInput, {
                                    ...inputProps,
                                    onValueChange: updateQuery,
                                    placeholder: searchPlaceholder,
                                    value: query
                                }),
                                /*#__PURE__*/ _jsxs(CommandList, {
                                    className: "max-h-[var(--component-overlay-list-max-height)]",
                                    ref: handleListRef,
                                    children: [
                                        /*#__PURE__*/ _jsx(CommandEmpty, {
                                            className: "py-[var(--space-4)] text-center text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                            children: loading ? loadingMessage : emptyMessage
                                        }),
                                        clearable && selectedValue ? /*#__PURE__*/ _jsx(CommandGroup, {
                                            children: /*#__PURE__*/ _jsx(CommandItem, {
                                                keywords: [
                                                    'clear',
                                                    'none',
                                                    'remove'
                                                ],
                                                onSelect: ()=>commit(undefined),
                                                value: "__specra_clear__",
                                                children: /*#__PURE__*/ _jsx(SpecraText, {
                                                    tone: "muted",
                                                    children: clearLabel
                                                })
                                            })
                                        }) : null,
                                        /*#__PURE__*/ _jsx(CommandGroup, {
                                            children: options.map((option, optionIndex)=>{
                                                const optionProps = getOptionProps?.(option);
                                                const optionText = option.textValue ?? (typeof option.label === 'string' ? option.label : undefined);
                                                const optionId = option.id ?? option.value;
                                                const renderIdentity = optionIdentityPlan.identities[optionIndex];
                                                return /*#__PURE__*/ _createElement(CommandItem, {
                                                    ...optionProps,
                                                    "aria-label": optionProps?.['aria-label'] ?? optionText,
                                                    className: cn(optionProps?.className),
                                                    "data-option-id": optionId,
                                                    "data-testid": getOptionTestId?.(option) ?? optionProps?.['data-testid'],
                                                    disabled: option.disabled,
                                                    key: renderIdentity,
                                                    keywords: [
                                                        ...optionText ? [
                                                            optionText
                                                        ] : [],
                                                        ...typeof option.description === 'string' ? [
                                                            option.description
                                                        ] : [],
                                                        ...option.keywords ?? []
                                                    ],
                                                    onSelect: ()=>commit(option.value, option),
                                                    value: `option:${renderIdentity}`
                                                }, option.content ?? /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        option.icon ? /*#__PURE__*/ _jsx("span", {
                                                            "aria-hidden": "true",
                                                            className: "shrink-0",
                                                            children: option.icon
                                                        }) : null,
                                                        /*#__PURE__*/ _jsxs("span", {
                                                            className: "min-w-0 flex-1",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(SpecraText, {
                                                                    className: "block truncate",
                                                                    children: option.label
                                                                }),
                                                                option.description ? /*#__PURE__*/ _jsx(SpecraText, {
                                                                    className: "mt-[var(--space-0-5)] block truncate",
                                                                    size: "micro",
                                                                    tone: "muted",
                                                                    children: option.description
                                                                }) : null
                                                            ]
                                                        })
                                                    ]
                                                }));
                                            })
                                        }),
                                        onCreate && (showCreate || isCreating) ? /*#__PURE__*/ _jsx(CommandGroup, {
                                            forceMount: true,
                                            children: /*#__PURE__*/ _jsxs(CommandItem, {
                                                "data-testid": createTestId,
                                                disabled: isCreating,
                                                forceMount: true,
                                                keywords: [
                                                    normalizedQuery
                                                ],
                                                onSelect: async ()=>{
                                                    if (!showCreate || createInFlightRef.current) return;
                                                    createInFlightRef.current = true;
                                                    setCreateError(undefined);
                                                    setInternalCreating(true);
                                                    try {
                                                        await onCreate(normalizedQuery);
                                                        updateOpen(false);
                                                        updateQuery('');
                                                    } catch (error) {
                                                        setCreateError(createErrorMessage);
                                                        onCreateError?.(error, normalizedQuery);
                                                    } finally{
                                                        createInFlightRef.current = false;
                                                        setInternalCreating(false);
                                                    }
                                                },
                                                value: "__specra_create__",
                                                children: [
                                                    /*#__PURE__*/ _jsx(PlusIcon, {
                                                        "aria-hidden": "true"
                                                    }),
                                                    isCreating ? createLoadingLabel : createLabel(normalizedQuery)
                                                ]
                                            })
                                        }) : null,
                                        createError ? /*#__PURE__*/ _jsx("div", {
                                            className: "px-[var(--space-2)] py-[var(--space-2)] text-[length:var(--fs-ui-caption)] text-danger",
                                            role: "alert",
                                            children: createError
                                        }) : null
                                    ]
                                })
                            ]
                        })
                    })
                ]
            }),
            showValidationError ? /*#__PURE__*/ _jsx("p", {
                className: "mt-[var(--space-1)] text-[length:var(--fs-ui-caption)] text-danger",
                id: validationId,
                role: "alert",
                children: validationMessage
            }) : null
        ]
    });
}
const SpecraComboboxRoot = /*#__PURE__*/ React.forwardRef(SpecraComboboxInner);
SpecraComboboxRoot.displayName = 'SpecraCombobox';
export const SpecraCombobox = SpecraComboboxRoot;
